<template>
  <div class="page-wrapper">
    <!-- Modern Header -->
    <header class="header">
      <div class="header-container">
        <router-link to="/" class="logo-link">
          <img src="@/assets/logo.png" alt="Nexthome4U Logo" class="logo" />
        </router-link>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <div class="forgot-card">
        <div class="card-header">
          <h1 class="title">Forgot Password?</h1>
          <p class="subtitle">Enter your email address and we'll send you a verification code</p>
        </div>

        <form class="form" @submit.prevent="submitForm">
          <div class="form-group">
            <label for="email" class="label">Email Address</label>
            <div class="input-wrapper">
              <input 
                type="email" 
                id="email" 
                v-model="email" 
                class="input"
                placeholder="Enter your email address"
                required 
                autocomplete="email"
              />
              <div class="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
            </div>
          </div>

          <button type="submit" class="submit-button" :disabled="isSubmitting">
            <span v-if="!isSubmitting">Send Verification Code</span>
            <div v-if="isSubmitting" class="loading-spinner">
              <div class="spinner"></div>
              <span>Sending...</span>
            </div>
          </button>

          <div class="divider">
            <span class="divider-text">Remember your password?</span>
          </div>

          <router-link to="/login" class="login-link">
            Back to Sign In
          </router-link>
        </form>
      </div>
    </main>

    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <p class="toast-message">{{ toast.message }}</p>
          <button class="toast-close" @click="removeToast(toast.id)">x</button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { waitForToken } from '@/utilities/tokenHelper'

export default {
  name: 'ForgotPasswordPage',
  data() {
    return {
      email: '',
      password: '',
      isSubmitting: false,
      toasts: []
    }
  },
  methods: {
    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type });
      if (duration > 0) {
        setTimeout(() => this.removeToast(id), duration);
      }
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },
    async submitForm() {
      this.isSubmitting = true;

      try {
        await waitForToken() // waits for token
        await apiService.sendEmailOtp(this.email);

        // Navigate to DashboardPage
        this.$router.push({ path: '/otp/verify', query: { email: this.email } });
      } catch (error) {
        console.error('Otp Send Failed error:', error);
        if (error.response) {
          // Handle specific API errors
          const errorData = error.response.data;
          const status = error.response.status;
          
          let message = null;
          // For HTTP 400 errors, always use the reason field from API response
          if (status === 400) {
            if (errorData?.reason) {
              message = errorData.reason;
            } else if (errorData?.message) {
              message = errorData.message;
            }
            // If no reason/message provided for 400 error, don't show toast (no generic messages)
          } else {
            // For other status codes, prioritize reason field
            if (errorData?.reason) {
              message = errorData.reason;
            } else if (errorData?.message) {
              message = errorData.message;
            } else {
              message = 'Please check your credentials';
            }
          }
          
          if (message) {
            this.showToast(`Otp Send Failed: ${message}`, 'error');
          }
        } else if (error.request) {
          this.showToast('No response from server. Please try again later.', 'error');
        } else {
          this.showToast(`Error: ${error.message}`, 'error');
        }
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>

* {
  box-sizing: border-box;
}

.page-wrapper {
  min-height: 100vh;
  background: #fcfbfb;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  position: sticky;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
}


.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.logo-link {
  display: inline-block;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: scale(1.05);
}

.logo {
  height: 40px;
  width: auto;
  max-width: 150px;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  min-height: calc(100vh - 80px);
}

.forgot-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 
    0 32px 64px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  padding: 3rem;
  width: 100%;
  max-width: 420px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.subtitle {
  color: #6b7280;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
}

/* Form */
.form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input {
  width: 100%;
  padding: 0.875rem 1rem;
  padding-right: 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 400;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  outline: none;
}

.input:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
  background: #ffffff;
}

.input:hover {
  border-color: #d1d5db;
}

.input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.input-icon {
  position: absolute;
  right: 1rem;
  color: #9ca3af;
  pointer-events: none;
}

.submit-button {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 48px;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.4);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.4);
}

.submit-button:active {
  transform: translateY(0);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
}

.divider-text {
  background: rgba(255, 255, 255, 0.95);
  color: #6b7280;
  font-size: 0.875rem;
  padding: 0 1rem;
  position: relative;
}

.login-link {
  display: block;
  text-align: center;
  color: #5b2e8e;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.975rem;
  padding: 0.75rem 1rem;
  border: 2px solid #5b2e8e;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.login-link:hover {
  background: #5b2e8e;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

/* Responsive Design */
@media (max-width: 640px) {
  .main-content {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .forgot-card {
    padding: 2rem 1.5rem;
    border-radius: 16px;
    margin-top: 1rem;
  }

  .title {
    font-size: 1.75rem;
  }
}

@media (max-width: 480px) {
  .forgot-card {
    padding: 1.5rem 1rem;
  }

  .title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.925rem;
  }
}

.toast-container {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10001;
  max-width: 380px;
}

.toast {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
  border-radius: 8px;
  background: #fff;
  border-left: 4px solid #ef4444;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.toast-error {
  border-left-color: #ef4444;
}

.toast-success {
  border-left-color: #10b981;
}

.toast-message {
  margin: 0;
  color: #374151;
  font-size: 14px;
}

.toast-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  font-size: 14px;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>